import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Layers, Camera } from 'lucide-react';

const AUTOPLAY_MS = 6000;

/**
 * Industrial / technical hero slider — steel frame, HUD labels, thumbnails, autoplay progress.
 */
const IndustrialHeroSlider = ({ slides = [] }) => {
  const [active, setActive] = useState(0);
  const n = slides.length || 1;

  useEffect(() => {
    if (n <= 1) return undefined;
    const t = setInterval(() => {
      setActive((prev) => (prev + 1) % n);
    }, AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [n]);

  const go = useCallback(
    (delta) => {
      setActive((prev) => (prev + delta + n) % n);
    },
    [n]
  );

  const goTo = useCallback((index) => {
    setActive(index);
  }, []);

  if (!slides.length) {
    return (
      <div className="flex min-h-[280px] items-center justify-center rounded-sm border border-white/10 bg-neutral-900/80 lg:min-h-[min(58vh,580px)]">
        <p className="text-sm text-slate-400">No project images</p>
      </div>
    );
  }

  const current = slides[active];

  return (
    <div className="relative isolate min-h-[280px] overflow-hidden lg:min-h-[min(58vh,580px)]">
      {/* Autoplay progress — technical strip */}
      <div
        className="absolute left-0 right-0 top-0 z-40 h-[3px] bg-white/10"
        role="presentation"
        aria-hidden
      >
        <div
          key={active}
          style={{ '--hero-autoplay-ms': `${AUTOPLAY_MS}ms` }}
          className="hero-autoplay-progress-bar h-full w-full bg-gradient-to-r from-amber-500 via-amber-400 to-sky-400/90"
        />
      </div>

      {/* Top HUD bar */}
      <div className="absolute left-0 right-0 top-[3px] z-30 flex items-center justify-between gap-2 border-b border-white/10 bg-[#050d14]/85 px-3 py-2 backdrop-blur-md sm:px-4">
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-300 sm:text-[11px]">
          <Camera className="h-3.5 w-3.5 text-amber-400/90" aria-hidden />
          <span className="hidden sm:inline">Site documentation</span>
          <span className="sm:hidden">Field</span>
        </div>
        <div className="flex items-center gap-2 font-mono text-[10px] tabular-nums text-amber-200/90 sm:text-xs">
          <Layers className="h-3.5 w-3.5 text-slate-400" aria-hidden />
          <span>
            {String(active + 1).padStart(2, '0')} / {String(n).padStart(2, '0')}
          </span>
        </div>
      </div>

      {/* Steel frame + corner brackets */}
      <div className="absolute inset-0 z-20 pointer-events-none">
        <div className="absolute inset-2 rounded-sm border border-white/15 sm:inset-3" />
        <div className="absolute left-3 top-12 h-10 w-10 border-l-2 border-t-2 border-amber-500/70 sm:left-5 sm:top-14" />
        <div className="absolute right-3 top-12 h-10 w-10 border-r-2 border-t-2 border-amber-500/70 sm:right-5 sm:top-14" />
        <div className="absolute bottom-24 left-3 h-10 w-10 border-b-2 border-l-2 border-amber-500/70 sm:left-5 sm:bottom-28" />
        <div className="absolute bottom-24 right-3 h-10 w-10 border-b-2 border-r-2 border-amber-500/70 sm:right-5 sm:bottom-28" />
      </div>

      {/* Vertical edge label — desktop */}
      <div
        className="pointer-events-none absolute bottom-32 left-1 z-20 hidden origin-bottom-left rotate-180 text-[9px] font-bold uppercase tracking-[0.35em] text-white/25 [writing-mode:vertical-rl] xl:block"
        aria-hidden
      >
        SVC · execution
      </div>

      {/* Image stack */}
      <div className="absolute inset-0 bg-[#030712]">
        {slides.map((slide, index) => (
          <img
            key={slide.url}
            src={slide.url}
            alt={slide.alt}
            loading={index === 0 ? 'eager' : 'lazy'}
            decoding="async"
            className={`absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-[900ms] ease-out ${
              index === active ? 'opacity-100 hero-slide-ken-active z-[1]' : 'opacity-0 z-0'
            }`}
          />
        ))}
      </div>

      {/* Light bottom fade only — keeps captions readable without darkening the photo */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] h-2/5 bg-gradient-to-t from-[#030712]/75 via-[#030712]/25 to-transparent"
        aria-hidden
      />

      {/* Bottom caption + controls */}
      <div className="absolute bottom-0 left-0 right-0 z-30 border-t border-white/10 bg-gradient-to-t from-[#030712]/80 via-[#030712]/40 to-transparent">
        <div className="flex flex-col gap-3 px-3 pb-3 pt-2 sm:px-4 sm:pb-4">
          <div className="flex min-h-[3rem] items-start gap-3 border-l-4 border-amber-500 bg-black/45 pl-3 pr-2 backdrop-blur-sm sm:min-h-[3.25rem] sm:pl-4">
            <div className="min-w-0 flex-1 pt-0.5">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-widest text-amber-200/90">
                Project frame · {String(active + 1).padStart(2, '0')}
              </p>
              <p className="mt-1 font-heading text-sm font-bold leading-snug text-white [text-shadow:0_1px_12px_rgba(0,0,0,0.85)] sm:text-base">
                {current.caption}
              </p>
            </div>
            <div className="flex shrink-0 gap-2 pt-0.5">
              <button
                type="button"
                onClick={() => go(-1)}
                className="flex h-10 w-10 items-center justify-center rounded-sm border border-white/25 bg-[#0a1624]/90 text-white shadow-lg transition hover:border-amber-400/60 hover:bg-[#0f1f32] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400"
                aria-label="Previous project image"
              >
                <ChevronLeft className="h-5 w-5" strokeWidth={2.2} />
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                className="flex h-10 w-10 items-center justify-center rounded-sm border border-white/25 bg-[#0a1624]/90 text-white shadow-lg transition hover:border-amber-400/60 hover:bg-[#0f1f32] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400"
                aria-label="Next project image"
              >
                <ChevronRight className="h-5 w-5" strokeWidth={2.2} />
              </button>
            </div>
          </div>

          {/* Film-strip thumbnails */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:thin]">
            <span className="hidden shrink-0 font-mono text-[9px] uppercase tracking-widest text-slate-500 sm:inline">
              Index
            </span>
            <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:gap-2">
              {slides.map((slide, index) => (
                <button
                  key={slide.url}
                  type="button"
                  onClick={() => goTo(index)}
                  className={`group relative h-11 w-14 shrink-0 overflow-hidden rounded-sm border-2 transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400 sm:h-12 sm:w-16 ${
                    index === active
                      ? 'border-amber-400 shadow-[0_0_0_1px_rgba(251,191,36,0.4)] ring-1 ring-amber-400/30'
                      : 'border-white/20 opacity-75 hover:border-white/45 hover:opacity-100'
                  }`}
                  aria-label={`Show project image ${index + 1}`}
                  aria-current={index === active ? 'true' : undefined}
                >
                  <img
                    src={slide.url}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                  <span
                    className={`absolute bottom-0 left-0 right-0 py-0.5 text-center font-mono text-[9px] font-bold text-white ${
                      index === active ? 'bg-amber-600/90' : 'bg-black/70'
                    }`}
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndustrialHeroSlider;
